import ApiHelper from "../../ApiHelper";
import API_ENDPOINTS from "../../endpoints";

const CERTIFICATE_BASE_URL = '/admin/certificates';

class CertificateHelper {
  // =====================================================
  // GET METHODS
  // =====================================================

  /**
   * Get all certificates with filters
   * @param {Object} filters - Filter options
   * @param {string} filters.college_id - Filter by college
   * @param {string} filters.course_id - Filter by course
   * @param {boolean} filters.is_approved - Filter by approval status
   * @param {boolean} filters.is_revoked - Filter by revocation status
   * @param {string} filters.from_date - Start date filter
   * @param {string} filters.to_date - End date filter
   * @param {string} filters.search - Search term
   * @param {number} filters.page - Page number
   * @param {number} filters.limit - Items per page
   */
  async getCertificates(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const url = queryString 
        ? `${CERTIFICATE_BASE_URL}?${queryString}` 
        : CERTIFICATE_BASE_URL;

      const response = await ApiHelper.get(url);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch certificates: ${error.message}`);
    }
  }

  /**
   * Get certificate by ID
   * @param {string} certificateId - Certificate UUID
   */
  async getCertificateById(certificateId) {
    try {
      const response = await ApiHelper.get(`${CERTIFICATE_BASE_URL}/${certificateId}`);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch certificate by ID: ${error.message}`);
    }
  }

  /**
   * Get pending approvals for college-course
   * @param {Object} params
   * @param {string} params.college_id - College UUID (required)
   * @param {string} params.course_id - Course UUID (required)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   */
  async getPendingApprovals(params) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });

      const response = await ApiHelper.get(
        `${CERTIFICATE_BASE_URL}/pending?${queryParams.toString()}`
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch pending approvals: ${error.message}`);
    }
  }

  /**
   * Get certificate statistics
   * @param {Object} filters - Optional filters
   */
  async getCertificateStats(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const url = queryString 
        ? `${CERTIFICATE_BASE_URL}/stats?${queryString}` 
        : `${CERTIFICATE_BASE_URL}/stats`;

      const response = await ApiHelper.get(url);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch certificate statistics: ${error.message}`);
    }
  }

  /**
   * Verify certificate by number
   * @param {string} certificateNumber - Certificate number
   */
  async verifyCertificate(certificateNumber) {
    try {
      const response = await ApiHelper.get(
        `${CERTIFICATE_BASE_URL}/verify/${certificateNumber}`
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to verify certificate: ${error.message}`);
    }
  }

  /**
   * Get colleges list for dropdown
   */
  async getColleges() {
    try {
      const response = await ApiHelper.get(`${CERTIFICATE_BASE_URL}/colleges`);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch colleges: ${error.message}`);
    }
  }

  /**
   * Get courses list for dropdown
   */
  async getCourses() {
    try {
      const response = await ApiHelper.get(`${CERTIFICATE_BASE_URL}/courses`);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch courses: ${error.message}`);
    }
  }

  // =====================================================
  // POST METHODS
  // =====================================================

  /**
   * Approve certificate for a single student
   * @param {Object} data
   * @param {string} data.student_id - Student UUID
   * @param {string} data.course_id - Course UUID
   */
  async approveCertificate(data) {
    try {
      const response = await ApiHelper.post(
        `${CERTIFICATE_BASE_URL}/approve`,
        data
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to approve certificate: ${error.message}`);
    }
  }

  /**
   * Issue a certificate manually to a student for a course (no enrollment check).
   * @param {Object} data
   * @param {string} data.student_id - Student UUID (required)
   * @param {string} data.course_id - Course UUID (required)
   * @returns {Promise} response with status 201 (created) or 200 (already exists)
   */
  async issueManualCertificate(data) {
    try {
      const response = await ApiHelper.post(
        `${CERTIFICATE_BASE_URL}/issue-manual`,
        data
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to issue manual certificate: ${error.message}`);
    }
  }

  /**
   * Bulk approve certificates for college-course
   * @param {Object} data
   * @param {string} data.college_id - College UUID
   * @param {string} data.course_id - Course UUID
   * @param {Array<string>} data.student_ids - Optional specific student IDs
   */
  async bulkApproveCertificates(data) {
    try {
      const response = await ApiHelper.post(
        `${CERTIFICATE_BASE_URL}/bulk-approve`,
        data
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to bulk approve certificates: ${error.message}`);
    }
  }

  /**
   * Revoke a certificate
   * @param {string} certificateId - Certificate UUID
   * @param {Object} data
   * @param {string} data.reason - Revocation reason
   */
  async revokeCertificate(certificateId, data) {
    try {
      const response = await ApiHelper.post(
        `${CERTIFICATE_BASE_URL}/${certificateId}/revoke`,
        data
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to revoke certificate: ${error.message}`);
    }
  }
  /**
   * List all students with certificate summaries
   * @param {Object} filters - See API docs for supported params
   */
  async getStudentsWithCertificates(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const qs = queryParams.toString();
      const url = qs
        ? `${API_ENDPOINTS.ADMIN.CERTIFICATE_STUDENTS}?${qs}`
        : API_ENDPOINTS.ADMIN.CERTIFICATE_STUDENTS;
      return await ApiHelper.get(url);
    } catch (error) {
      throw new Error(`Failed to fetch students: ${error.message}`);
    }
  }

  /**
   * Get full student profile + all their certificates
   * @param {string} studentId
   */
  async getStudentCertificateDetail(studentId) {
    try {
      return await ApiHelper.get(API_ENDPOINTS.ADMIN.CERTIFICATE_STUDENT_BY_ID(studentId));
    } catch (error) {
      throw new Error(`Failed to fetch student certificate detail: ${error.message}`);
    }
  }
}

export default new CertificateHelper();